import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, resolveAssetUrl } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, BarChart3, Mail } from "lucide-react";

const emptyForm = {
  name: "",
  price: "",
  category: "tops",
  image_url: "",
  gallery: "",
  description: "",
  badge: "",
  active: true,
  variants: [],
};

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const COMMON_COLORS = ["Black", "Silver", "Stone", "Olive", "White"];

const Admin = () => {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [p, o, s] = await Promise.all([
        api.get("/products?active_only=false"),
        api.get("/orders"),
        api.get("/admin/stats"),
      ]);
      setProducts(p.data || []);
      setOrders(o.data || []);
      setStats(s.data || null);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      price: p.price,
      category: p.category,
      image_url: p.image_url,
      gallery: (p.gallery || []).join("\n"),
      description: p.description || "",
      badge: p.badge || "",
      active: p.active,
      variants: p.variants || [],
    });
    setModalOpen(true);
  };

  const addVariant = () => {
    setForm({
      ...form,
      variants: [...form.variants, { size: "M", color: "Black", stock: 10 }],
    });
  };
  const updateVariant = (idx, patch) => {
    const next = [...form.variants];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, variants: next });
  };
  const removeVariant = (idx) => {
    setForm({ ...form, variants: form.variants.filter((_, i) => i !== idx) });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const gallery = form.gallery
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        name: form.name,
        price: parseFloat(form.price),
        category: form.category,
        image_url: form.image_url,
        gallery,
        description: form.description,
        badge: form.badge || null,
        active: form.active,
        variants: form.variants.map((v) => ({
          size: v.size,
          color: v.color,
          stock: parseInt(v.stock || 0, 10),
        })),
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setModalOpen(false);
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const triggerEmails = async () => {
    try {
      await api.post("/admin/trigger-abandoned-emails");
      toast.success("Abandoned cart job triggered");
    } catch (e) {
      toast.error("Trigger failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white">
      <header className="border-b border-zinc-800 bg-black/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link
            to="/"
            className="flex items-center space-x-3"
            data-testid="admin-home-link"
          >
            <img
              src={resolveAssetUrl(BRAND.LOGO)}
              alt="Faith Athletic"
              className="h-10 w-10 object-contain"
            />
            <span className="font-heading text-sm font-bold tracking-widest border-l pl-3 border-zinc-700 hidden sm:inline-block">
              FAITH ATHLETIC{" "}
              <span className="text-[10px] block tracking-[0.2em] text-zinc-400">
                ADMIN
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-6 text-xs font-bold tracking-widest uppercase">
            <button
              onClick={() => setTab("dashboard")}
              className={
                tab === "dashboard"
                  ? "border-b-2 border-white pb-1"
                  : "text-zinc-500"
              }
              data-testid="admin-tab-dashboard"
            >
              Dashboard
            </button>
            <button
              onClick={() => setTab("products")}
              className={
                tab === "products"
                  ? "border-b-2 border-white pb-1"
                  : "text-zinc-500"
              }
              data-testid="admin-tab-products"
            >
              Products
            </button>
            <button
              onClick={() => setTab("orders")}
              className={
                tab === "orders"
                  ? "border-b-2 border-white pb-1"
                  : "text-zinc-500"
              }
              data-testid="admin-tab-orders"
            >
              Orders
            </button>
            <span className="text-zinc-600 hidden lg:inline">
              {user?.email}
            </span>
            <button
              onClick={() => logout()}
              className="text-zinc-400 hover:text-white inline-flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {tab === "dashboard" && (
          <>
            <h1 className="font-heading text-4xl font-bold uppercase tracking-tight mb-8">
              Command Center
            </h1>
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
              data-testid="admin-stats-grid"
            >
              {[
                { label: "Revenue", value: `$${(stats?.revenue || 0).toFixed(2)}`, key: "revenue" },
                { label: "Orders", value: stats?.orders ?? 0, key: "orders" },
                { label: "Products", value: stats?.active_products ?? 0, key: "products" },
                { label: "Subscribers", value: stats?.subscribers ?? 0, key: "subs" },
                { label: "Abandoned", value: stats?.abandoned_carts ?? 0, key: "abandoned" },
                { label: "Active SKUs", value: stats?.active_products ?? 0, key: "skus" },
              ].map((s) => (
                <div
                  key={s.key}
                  className="border border-zinc-800 bg-zinc-950 p-5"
                  data-testid={`stat-${s.key}`}
                >
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
                    {s.label}
                  </div>
                  <div className="font-heading text-3xl font-black tracking-tight">
                    {s.value}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="border border-zinc-800 bg-zinc-950 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <BarChart3 size={18} className="text-zinc-400" />
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight">
                    Quick Actions
                  </h3>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={openNew}
                    className="w-full text-left text-xs tracking-widest uppercase border border-zinc-700 py-3 px-4 hover:bg-zinc-900"
                    data-testid="dashboard-action-new-product"
                  >
                    + New Product
                  </button>
                  <button
                    onClick={triggerEmails}
                    className="w-full text-left text-xs tracking-widest uppercase border border-zinc-700 py-3 px-4 hover:bg-zinc-900 flex items-center gap-2"
                    data-testid="dashboard-action-trigger-emails"
                  >
                    <Mail size={14} /> Process Abandoned Cart Emails
                  </button>
                </div>
              </div>

              <div className="border border-zinc-800 bg-zinc-950 p-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-4">
                  Recent Orders
                </h3>
                {orders.length === 0 ? (
                  <div className="text-zinc-600 text-xs uppercase tracking-widest">
                    No orders yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {orders.slice(0, 5).map((o) => (
                      <div
                        key={o.id}
                        className="flex justify-between text-xs border-b border-zinc-900 pb-2"
                      >
                        <span className="text-zinc-500 font-mono">
                          {o.session_id?.slice(0, 16)}...
                        </span>
                        <span className="font-bold">
                          ${Number(o.amount).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {tab === "products" && (
          <>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">
                  Product Inventory
                </h1>
                <p className="text-xs text-zinc-500 tracking-widest uppercase mt-1">
                  {products.length} items
                </p>
              </div>
              <button
                onClick={openNew}
                className="bg-white text-black font-heading font-bold text-xs tracking-widest uppercase px-5 py-3 hover:bg-zinc-300 inline-flex items-center gap-2"
                data-testid="admin-new-product-btn"
              >
                <Plus size={14} /> New Product
              </button>
            </div>

            <div className="border border-zinc-800 overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="admin-products-table"
              >
                <thead className="bg-zinc-900 text-xs uppercase tracking-widest text-zinc-400">
                  <tr>
                    <th className="text-left px-4 py-3">Product</th>
                    <th className="text-left px-4 py-3">Category</th>
                    <th className="text-left px-4 py-3">Price</th>
                    <th className="text-left px-4 py-3">Stock</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const totalStock = (p.variants || []).reduce(
                      (s, v) => s + (v.stock || 0),
                      0
                    );
                    const lowStock = totalStock < 10 && (p.variants || []).length > 0;
                    return (
                      <tr
                        key={p.id}
                        className="border-t border-zinc-900 hover:bg-zinc-900/40"
                        data-testid={`admin-product-row-${p.id}`}
                      >
                        <td className="px-4 py-3 flex items-center gap-3">
                          <img
                            src={resolveAssetUrl(p.image_url)}
                            alt={p.name}
                            className="w-10 h-12 object-cover bg-zinc-900"
                          />
                          <div>
                            <div className="font-bold text-sm uppercase tracking-wide">
                              {p.name}
                            </div>
                            {p.badge && (
                              <div className="text-[10px] text-zinc-500 tracking-widest uppercase">
                                {p.badge}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-zinc-400 uppercase text-xs tracking-wider">
                          {p.category}
                        </td>
                        <td className="px-4 py-3 font-bold">
                          ${Number(p.price).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs font-bold ${
                              lowStock ? "text-amber-400" : "text-zinc-300"
                            }`}
                          >
                            {totalStock}
                          </span>
                          <span className="text-zinc-600 text-[10px] ml-1">
                            ({(p.variants || []).length} SKUs)
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-[10px] tracking-widest uppercase px-2 py-1 ${
                              p.active
                                ? "bg-emerald-900/40 text-emerald-400"
                                : "bg-zinc-800 text-zinc-500"
                            }`}
                          >
                            {p.active ? "Active" : "Hidden"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openEdit(p)}
                            className="text-zinc-400 hover:text-white mr-3"
                            aria-label="Edit"
                            data-testid={`admin-edit-${p.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => del(p.id)}
                            className="text-zinc-400 hover:text-red-500"
                            aria-label="Delete"
                            data-testid={`admin-delete-${p.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {products.length === 0 && (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center text-zinc-500 py-12"
                      >
                        No products yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "orders" && (
          <>
            <div className="mb-8">
              <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">
                Recent Orders
              </h1>
              <p className="text-xs text-zinc-500 tracking-widest uppercase mt-1">
                {orders.length} paid order{orders.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="border border-zinc-800 overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="admin-orders-table"
              >
                <thead className="bg-zinc-900 text-xs uppercase tracking-widest text-zinc-400">
                  <tr>
                    <th className="text-left px-4 py-3">Order ID</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Items</th>
                    <th className="text-left px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr
                      key={o.id}
                      className="border-t border-zinc-900"
                      data-testid={`admin-order-row-${o.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-zinc-400">
                        {o.session_id?.slice(0, 14)}...
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-300">
                        {o.email || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {(o.items || [])
                          .map(
                            (i) =>
                              `${i.name}${
                                i.size ? ` (${i.size}/${i.color})` : ""
                              } ×${i.quantity}`
                          )
                          .join(", ")}
                      </td>
                      <td className="px-4 py-3 font-bold">
                        ${Number(o.amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">
                        {new Date(o.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr>
                      <td
                        colSpan="5"
                        className="text-center text-zinc-500 py-12"
                      >
                        No orders yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
          onClick={() => setModalOpen(false)}
          data-testid="admin-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center pb-4 border-b border-zinc-800">
              <h2 className="font-heading text-2xl font-bold uppercase">
                {editing ? "Edit Product" : "New Product"}
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
                data-testid="admin-modal-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                Name
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                data-testid="admin-form-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                  Price (USD)
                </label>
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                  data-testid="admin-form-price"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                  data-testid="admin-form-category"
                >
                  <option value="tops">Tops</option>
                  <option value="compression">Compression</option>
                  <option value="accessories">Accessories</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                Main Image URL
              </label>
              <input
                required
                value={form.image_url}
                onChange={(e) =>
                  setForm({ ...form, image_url: e.target.value })
                }
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                data-testid="admin-form-image"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                Gallery URLs (one per line)
              </label>
              <textarea
                rows="2"
                value={form.gallery}
                onChange={(e) => setForm({ ...form, gallery: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs focus:border-zinc-500 outline-none font-mono"
                data-testid="admin-form-gallery"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                Description
              </label>
              <textarea
                rows="3"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                data-testid="admin-form-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                  Badge (optional)
                </label>
                <input
                  value={form.badge}
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  placeholder="e.g. Elite"
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                  data-testid="admin-form-badge"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                  Status
                </label>
                <select
                  value={form.active ? "1" : "0"}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.value === "1" })
                  }
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:border-zinc-500 outline-none"
                  data-testid="admin-form-active"
                >
                  <option value="1">Active</option>
                  <option value="0">Hidden</option>
                </select>
              </div>
            </div>

            {/* Variants */}
            <div className="space-y-2 pt-2 border-t border-zinc-900">
              <div className="flex justify-between items-center">
                <label className="text-[10px] tracking-widest uppercase text-zinc-500 block">
                  Variants ({form.variants.length})
                </label>
                <button
                  type="button"
                  onClick={addVariant}
                  className="text-xs uppercase tracking-widest border border-zinc-700 px-3 py-1 hover:bg-zinc-900"
                  data-testid="admin-variant-add"
                >
                  + Add SKU
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {form.variants.map((v, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`admin-variant-row-${i}`}
                  >
                    <select
                      value={v.size}
                      onChange={(e) =>
                        updateVariant(i, { size: e.target.value })
                      }
                      className="col-span-3 bg-zinc-900 border border-zinc-800 px-2 py-2 text-xs"
                    >
                      {SIZES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <input
                      value={v.color}
                      onChange={(e) =>
                        updateVariant(i, { color: e.target.value })
                      }
                      list={`colors-${i}`}
                      placeholder="Color"
                      className="col-span-5 bg-zinc-900 border border-zinc-800 px-2 py-2 text-xs"
                    />
                    <datalist id={`colors-${i}`}>
                      {COMMON_COLORS.map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                    <input
                      type="number"
                      min="0"
                      value={v.stock}
                      onChange={(e) =>
                        updateVariant(i, { stock: e.target.value })
                      }
                      placeholder="Stock"
                      className="col-span-3 bg-zinc-900 border border-zinc-800 px-2 py-2 text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => removeVariant(i)}
                      className="col-span-1 text-zinc-500 hover:text-red-500"
                      aria-label="Remove variant"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {form.variants.length === 0 && (
                  <p className="text-xs text-zinc-600">
                    No variants. Add SKUs to enable inventory tracking.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="flex-1 border border-zinc-700 text-zinc-300 font-heading font-bold text-xs tracking-widest uppercase py-3 hover:bg-zinc-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-white text-black font-heading font-bold text-xs tracking-widest uppercase py-3 hover:bg-zinc-300 disabled:opacity-50"
                data-testid="admin-form-save"
              >
                {saving ? "Saving..." : editing ? "Save Changes" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Admin;
